import { useCallback, useMemo } from 'react';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { useService } from '../../contexts/ServiceContext';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { useMetadata } from '../../contexts/MetadataContext';
import { usePreferences, PreferenceKey } from '../../hooks/usePreferences';
import { useTenantPath } from '../../hooks/useTenantPath';
import { KEYSTONE_REDIRECT_URL } from '../../config';
import type { OverviewConfig } from '@brightmls/nextgen.lae.overview';
import { CityZipCodeField, ResoResource } from '../../system/reso';
import { CityZipCodeResource } from '../../reso/resources/CityZipCodeResource';
import { useResoResource } from '../../reso/useResoResource';
import { RESO_RESOURCE_PAGE_SIZE } from '../../config';

import { OVERVIEW_FIELDS } from './overviewFields';
import { buildFieldRegistry } from './buildFieldRegistry';
import { useTenant } from '../../contexts/TenantContext';
import { usePromoteService } from '../../contexts/PromoteServiceContext';
import { useAuth } from '../../contexts/AuthContext';
import { isPersonalAssistant } from '../../contexts/util/createParams';

export const useOverviewConfig = () => {
  const { canCreateListing, allowed, session } = useAuth();
  const {
    searchListingsPaginated,
    isWebSocketReady,
    searchLookups,
    saveListing,
    getListingHistory,
    findAgent,
    getDelegatableAgents,
  } = useService();
  const { hasFeature, getFeatureValue, setTrait } = useFeatureFlags();
  const { isConnected } = useWebSocketContext();
  const {
    field: getFieldMetadata,
    getFieldByLocaleKey,
    lookup,
    displayValueForListingField,
  } = useMetadata();
  const { getPath } = useTenantPath();
  const { usePreference } = usePreferences();
  const { tenant, supportsClientResoWebApi, getConfigValue } = useTenant();
  const { getListingInsights, getActiveAds } = usePromoteService();

  // RESO CityZipCode resource — shared across all three address cascade services
  const { findListByField, findListByFields } =
    useResoResource<CityZipCodeResource>(ResoResource.CITY_ZIP_CODE, {
      enableCache: true,
      autoPage: RESO_RESOURCE_PAGE_SIZE,
    });

  // County search: unique counties for a given state
  const searchCounties = useCallback(
    async (state: string, _signal?: AbortSignal) => {
      if (!state || state === 'all') return [];
      const results = await findListByField(CityZipCodeField.STATE, state);
      const seen = new Set<string>();
      return results
        .filter((r) => {
          const c = r[CityZipCodeField.COUNTY];
          if (!c || seen.has(c)) return false;
          seen.add(c);
          return true;
        })
        .sort((a, b) =>
          a[CityZipCodeField.COUNTY].localeCompare(b[CityZipCodeField.COUNTY]),
        )
        .map((r) => ({
          value: r[CityZipCodeField.COUNTY],
          label: r[CityZipCodeField.COUNTY],
        }));
    },
    [findListByField],
  );

  // City search: one row per distinct city+zip pair so the SidePanel can
  // decorate duplicate city names with the zip and populate POSTAL_CODE
  // when the user picks a city.
  const searchCities = useCallback(
    async (state: string, county: string, _signal?: AbortSignal) => {
      if (!state || !county || state === 'all' || county === 'all') return [];
      const results = await findListByFields({
        [CityZipCodeField.STATE]: state,
        [CityZipCodeField.COUNTY]: county,
      });
      const seen = new Set<string>();
      return results
        .filter((r) => {
          const c = r[CityZipCodeField.CITY_NAME];
          const z = r[CityZipCodeField.ZIP];
          if (!c || !z) return false;
          const key = `${c}|${z}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .sort((a, b) => {
          const byCity = a[CityZipCodeField.CITY_NAME].localeCompare(
            b[CityZipCodeField.CITY_NAME],
          );
          return byCity !== 0
            ? byCity
            : a[CityZipCodeField.ZIP].localeCompare(b[CityZipCodeField.ZIP]);
        })
        .map((r) => ({
          value: r[CityZipCodeField.CITY_NAME].toUpperCase(),
          label: r[CityZipCodeField.CITY_NAME].toUpperCase(),
          zipCode: r[CityZipCodeField.ZIP],
        }));
    },
    [findListByFields],
  );

  // Postal code search: unique zips for a given state + county, with cityName for CITY auto-population
  const searchPostalCodes = useCallback(
    async (state: string, county: string, _signal?: AbortSignal) => {
      if (!state || !county || state === 'all' || county === 'all') return [];
      const results = await findListByFields({
        [CityZipCodeField.STATE]: state,
        [CityZipCodeField.COUNTY]: county,
      });
      const seen = new Set<string>();
      return results
        .filter((r) => {
          const z = r[CityZipCodeField.ZIP];
          if (!z || seen.has(z)) return false;
          seen.add(z);
          return true;
        })
        .sort((a, b) =>
          a[CityZipCodeField.ZIP].localeCompare(b[CityZipCodeField.ZIP]),
        )
        .map((r) => ({
          value: r[CityZipCodeField.ZIP],
          label: r[CityZipCodeField.ZIP],
          cityName: r[CityZipCodeField.CITY_NAME].toUpperCase(),
        }));
    },
    [findListByFields],
  );

  // Preference hooks for Overview
  const itemsPerPagePref = usePreference(
    PreferenceKey.OVERVIEW_ITEMS_PER_PAGE,
    20,
  );
  const viewModePref = usePreference(
    PreferenceKey.OVERVIEW_VIEW_MODE,
    'grid' as string,
  );

  // Field cache for resource adapter
  const fieldCache = useMemo(() => new Map<string, any>(), []);

  // Resource adapter.
  // Overview doesn't operate on a single listing, so no FLC fields are
  // pre-populated, but the cache + get/set structure is in place for any
  // fields the package may request.
  const resource = useMemo(
    () => ({
      get: (fieldName: string) => {
        if (fieldCache.has(fieldName)) {
          return fieldCache.get(fieldName);
        }

        // Return a default field state matching the FieldState interface
        return {
          value: undefined,
          isDirty: false,
          isValid: true,
          errors: [],
          warnings: [],
          isHidden: false,
          isRequired: false,
          addToList: [],
          removeFromList: [],
          renderCount: 0,
          hasErrors: false,
          hasWarnings: false,
          isDisabled: false,
          updateValue: (value: any) => {
            console.warn(`Overview: no field handler for ${fieldName}:`, value);
          },
        };
      },
      set: (fieldName: string, value: any) => {
        const field = fieldCache.get(fieldName);
        if (field?.updateValue) {
          field.updateValue(value);
        }
      },
    }),
    [fieldCache],
  );

  // Build the field registry from the declarative field array + metadata.
  // Auto-detects resolution type (static / dynamic / none) per field
  // based on FieldMetadata — same array works across all tenants.
  const fieldRegistry = useMemo(
    () =>
      buildFieldRegistry(OVERVIEW_FIELDS, {
        getFieldMetadata: (key: string) => {
          // Try by Key first (e.g., 'MLS_STATUS'), fall back to LocaleKey
          return getFieldMetadata(key) || getFieldByLocaleKey(key);
        },
        lookup,
        displayValueForListingField,
        searchLookups,
      }),
    [
      getFieldMetadata,
      getFieldByLocaleKey,
      lookup,
      displayValueForListingField,
      searchLookups,
    ],
  );

  // Overview configuration
  // NOTE: fieldRegistry is passed here but the package type doesn't include it yet.
  // Once the package adds `fieldRegistry?: FieldRegistry` to OverviewConfig,
  // the type assertion can be removed.
  const overviewConfig: OverviewConfig = useMemo(
    () =>
      ({
        tenant,
        supportsClientResoWebApi: supportsClientResoWebApi(),
        routing: {
          basePath: getPath('/overview'),
          paths: {
            history: 'history',
            create: getPath('/create'),
            edit: getPath('/edit'),
          },
        },
        features: {
          NGW_OVERVIEW: hasFeature(FeatureFlags.NGW_OVERVIEW),
          // Permission-driven (not a feature flag): hides the "New Listing"
          // button in the Overview header for users without CREATE_LISTING.
          NGW_CAN_CREATE_LISTING: canCreateListing(),
          NGW_OVERVIEW_SIMPLE_SEARCH: hasFeature(
            FeatureFlags.NGW_OVERVIEW_SIMPLE_SEARCH,
          ),
          NGW_OVERVIEW_PRIMARY_SEARCH: hasFeature(
            FeatureFlags.NGW_OVERVIEW_PRIMARY_SEARCH,
          ),
          NGW_OVERVIEW_COPY_LISTING: hasFeature(
            FeatureFlags.NGW_OVERVIEW_COPY_LISTING,
          ),
          ...(hasFeature(FeatureFlags.PROMOTE_ENROLLMENT) && {
            PROMOTE_ENROLLMENT: getFeatureValue(
              FeatureFlags.PROMOTE_ENROLLMENT,
            ),
          }),
          NGW_BACK_TO_LEGACY: hasFeature(FeatureFlags.NGW_BACK_TO_LEGACY),
          NGW_USE_TENANT_PATHING: hasFeature(
            FeatureFlags.NGW_USE_TENANT_PATHING,
          ),
          NGW_ENROLLMENT: hasFeature(FeatureFlags.NGW_ENROLLMENT),
          NGW_OVERVIEW_PROMOTE_STATS: hasFeature(
            FeatureFlags.NGW_OVERVIEW_PROMOTE_STATS,
          ),
          NGW_OVERVIEW_PRINT_PROFILE: hasFeature(
            FeatureFlags.NGW_OVERVIEW_PRINT_PROFILE,
          ),
          NGW_OVERVIEW_FILTERS: getFeatureValue(
            FeatureFlags.NGW_OVERVIEW_FILTERS,
          ),
          NGW_OVERVIEW_HISTORY: hasFeature(FeatureFlags.NGW_OVERVIEW_HISTORY),
          NGW_OVERVIEW_OFFICE_EXCLUSIVE: hasFeature(
            FeatureFlags.NGW_OVERVIEW_OFFICE_EXCLUSIVE,
          ),
          NGW_OVERVIEW_OFF_MARKET_FILTERS: hasFeature(
            FeatureFlags.NGW_OVERVIEW_OFF_MARKET_FILTERS,
          ),
          NGW_DEBUG_OVERVIEW: hasFeature(FeatureFlags.NGW_DEBUG_OVERVIEW),
          NGW_OVERVIEW_PERMISSIONS_DROPDOWN: hasFeature(
            FeatureFlags.NGW_OVERVIEW_PERMISSIONS_DROPDOWN,
          ),
          NGW_OVERVIEW_EDIT_AS: hasFeature(FeatureFlags.NGW_OVERVIEW_EDIT_AS),
          NGW_OVERVIEW_MANAGE_ACCESS: hasFeature(
            FeatureFlags.NGW_OVERVIEW_MANAGE_ACCESS,
          ),
          NGW_OVERVIEW_PERSONAL_ASSISTANT: hasFeature(
            FeatureFlags.NGW_OVERVIEW_PERSONAL_ASSISTANT,
          ),
        },
        manageAccessUrl: getConfigValue<string>('MANAGE_ACCESS_URL'),
        // Bright: MemberType === 'Broker' gates broker-only entries inside
        // the Overview Permissions dropdown (currently just Manage Access).
        // Other tenants can expand this once they define their equivalents.
        isBroker: session?.user?.agent?.MemberType === 'Broker',
        // Bright: MemberType === 'Personal Assistant' — reuses the same
        // helper the /create flow already uses to detect PAs.
        isPersonalAssistant: isPersonalAssistant(session),
        promoteBaseUrl: getConfigValue<string>('PROMOTE_BASE_URL'),
        legacyUrl: KEYSTONE_REDIRECT_URL,
        resource,
        preferences: {
          itemsPerPage: itemsPerPagePref,
          viewMode: viewModePref,
        },
        sidePanel: {
          maxHeight: '75vh',
          headerVar: '--bright-header-height',
          footerVar: '--bright-footer-height',
        },
        fieldRegistry,
      }) as OverviewConfig,
    [
      tenant,
      supportsClientResoWebApi,
      hasFeature,
      getFeatureValue,
      canCreateListing,
      getPath,
      resource,
      session?.user?.agent?.MemberType,
      itemsPerPagePref,
      viewModePref,
      fieldRegistry,
    ],
  );

  // Keep lookup and displayValueForListingField on services until the
  // package updates OverviewServices to make them optional (Phase 1).
  // Once the package consumes fieldRegistry and falls back gracefully,
  // these can be removed (Phase 2, step 4).
  const overviewServices = useMemo(
    () => ({
      searchListingsPaginated,
      isWebSocketReady: () => isWebSocketReady() && isConnected,
      lookup,
      displayValueForListingField,
      deleteListing: async (listingKey: string) => {
        await saveListing('DELETE', listingKey, { Property: {} });
      },
      setTrait,
      getListingInsights,
      getActiveAds,
      getListingHistory,
      // Tag overview-originated agent lookups with `?isFromOverview=true` so
      // the backend can differentiate them from edit-flow agent searches
      // (telemetry / tenant-side gating). Other callers of findAgent stay
      // untagged.
      findAgent: (
        agentId: string,
        isScopedToBrokerage?: boolean,
        signal?: AbortSignal,
      ) => findAgent(agentId, isScopedToBrokerage, signal, true),
      getDelegatableAgents,
      searchCounties,
      searchCities,
      searchPostalCodes,
      // Strip Overview's `listing` arg at the boundary — AuthContext.allowed
      // takes { key, level? } only. Today the gating is global; if per-listing
      // semantics ever land, the signature is already plumbed through.
      allowed: (check: { key: string }) => allowed({ key: check.key as never }),
    }),
    [
      searchListingsPaginated,
      lookup,
      displayValueForListingField,
      isWebSocketReady,
      isConnected,
      saveListing,
      setTrait,
      getListingInsights,
      getActiveAds,
      getListingHistory,
      findAgent,
      getDelegatableAgents,
      searchCounties,
      searchCities,
      searchPostalCodes,
      allowed,
    ],
  );

  return {
    overviewConfig,
    overviewServices,
    hasFeature,
  };
};
